// src/pages/AddChildren.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  ArrowLeft,
  Users,
  Plus,
  Trash2,
  Crown,
  DollarSign,
  Tag,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { COURSE_PACKAGES, getSortedPackages } from '../config/coursePackages';
import { enrollMultipleChildren, validateDiscountCode, stripePromise } from '../config/stripe';
import { resolveDiscount } from '../utils/discountPricing';

// Non-adult packages, in sort order. This is the default set — the page only
// enrols children, and an adult account is turned away before the form renders
// (see the profile branch in the component).
const FAMILY_PACKAGES = getSortedPackages().filter((p) => p.audience !== 'adult');
// Hard-coded, NOT FAMILY_PACKAGES[0].id — the pre-selected package is a live
// choice about what customers buy by default, and it must not change silently
// if someone reorders sortOrder in coursePackages.js. This is the value the
// page used before; it happens to equal FAMILY_PACKAGES[0].id today.
const DEFAULT_FAMILY_PACKAGE_ID = 'course_6week_group';

function AddChildren() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── Account type, three states (ADDITION A — fail open) ────────────────────
  // Items 1 & 2 make this page depend on a getDoc that can fail. The three
  // states are handled deliberately differently:
  //   loading → spinner only. A half-loaded page decides nothing.
  //   loaded  → filter packages / redirect per accountType.
  //   failed  → treat the account as FAMILY: render the page, show the
  //             non-adult packages, let them enrol.
  // The `failed` direction is the whole point. A transient Firestore read
  // failure must not break "enrol another child" for every paying family in
  // order to protect six adult accounts. Worst case of failing open: an adult
  // account briefly sees child packages — and enrollMultipleChildren's server
  // guard rejects that at checkout regardless. Do not "fix" this to fail closed.
  const [profile, setProfile] = useState({ status: 'loading', data: null });

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (!cancelled) {
          setProfile({ status: 'loaded', data: snap.exists() ? snap.data() : null });
        }
      } catch (err) {
        console.warn('AddChildren: profile read failed — treating account as family (fail open):', err);
        if (!cancelled) setProfile({ status: 'failed', data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Positive test ONLY — absent / null / 'family' / anything unexpected is a
  // family account. Legacy accounts predate accountType entirely.
  const accountIsAdult = profile.status === 'loaded' && profile.data?.accountType === 'adult';

  // Asymmetric by design — NOT CourseRenewal's symmetric
  // `audience === 'child' : audience === 'adult'`. A future child package
  // shipped without an `audience` field must fall toward being VISIBLE to
  // families (`!== 'adult'`), not invisible to everyone. A package nobody can
  // buy, with no error, is the worse bug. Convenience only — the server guard
  // in enrollMultipleChildren is the enforcement point.
  const availablePackages = accountIsAdult
    ? getSortedPackages().filter((p) => p.audience === 'adult')
    : FAMILY_PACKAGES;

  // State for children being added
  const [children, setChildren] = useState([
    { name: '', dateOfBirth: null, packageId: DEFAULT_FAMILY_PACKAGE_ID }
  ]);

  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Discount state
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidation, setDiscountValidation] = useState(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);

  // Add a new child form
  const handleAddChild = () => {
    if (children.length < 5) {
      setChildren([...children, { name: '', dateOfBirth: null, packageId: DEFAULT_FAMILY_PACKAGE_ID }]);
    }
  };

  // Remove a child form
  const handleRemoveChild = (index) => {
    if (children.length > 1) {
      const updated = children.filter((_, i) => i !== index);
      setChildren(updated);
    }
  };

  // Update child data
  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index] = { ...updated[index], [field]: value };
    setChildren(updated);
    
    // Clear validation error for this field
    const newErrors = { ...validationErrors };
    delete newErrors[`${index}_${field}`];
    setValidationErrors(newErrors);
  };

  // Validate age (3 years old)
  const validateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'Date of birth is required';
    
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    if (age < 3) return 'Child must be at least 3 years old';
    
    return null;
  };

  // Validate all children
  const validateAllChildren = () => {
    const errors = {};
    let isValid = true;

    children.forEach((child, index) => {
      // Validate name
      if (!child.name || child.name.trim() === '') {
        errors[`${index}_name`] = 'Name is required';
        isValid = false;
      }

      // Validate date of birth
      if (!child.dateOfBirth) {
        errors[`${index}_dateOfBirth`] = 'Date of birth is required';
        isValid = false;
      } else {
        const ageError = validateAge(child.dateOfBirth);
        if (ageError) {
          errors[`${index}_dateOfBirth`] = ageError;
          isValid = false;
        }
      }

      // Validate package
      if (!child.packageId || !COURSE_PACKAGES[child.packageId]) {
        errors[`${index}_packageId`] = 'Please select a valid package';
        isValid = false;
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  // Real basket subtotal — the sum of each child's actually-selected package.
  const subtotal = children.reduce((sum, child) => {
    const pkg = COURSE_PACKAGES[child.packageId];
    return sum + (pkg ? pkg.price : 0);
  }, 0);

  // Discount priced against the REAL (possibly mixed-package) basket — see
  // src/utils/discountPricing.js. The old firstPackage×count shortcut showed a
  // figure that didn't match what Stripe charged on a mixed basket.
  const discount = resolveDiscount(
    discountValidation?.valid ? discountValidation.coupon : null,
    subtotal,
  );
  const discountApplies =
    !!discountValidation?.valid && !discount.wrongCurrency && discount.amount > 0;
  const discountAmount = discountApplies ? discount.amount : 0;

  const calculateTotal = () => Math.max(0, subtotal - discountAmount);

  // Handle discount code validation
  const handleValidateDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountValidation(null);
      return;
    }

    setIsValidatingDiscount(true);
    try {
      // Validate the code and get its coupon back only — the displayed discount
      // is priced by resolveDiscount against the real basket, so this call's own
      // discountAmount/finalAmount are unused. The real basket subtotal (cents)
      // IS sent so the server can check a code's minimum_amount against a
      // mixed-package basket. Package + quantity 1 are just a valid shape.
      const result = await validateDiscountCode(
        discountCode, children[0].packageId, 1, Math.round(subtotal * 100),
      );
      setDiscountValidation(result);
    } catch (error) {
      setDiscountValidation({
        valid: false,
        error: error.message || 'Invalid discount code'
      });
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  // Handle enrollment submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateAllChildren()) {
      setError('Please fix the errors below before continuing');
      return;
    }

    if (!user) {
      setError('You must be logged in to add children');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      console.log('🎯 Enrolling children:', children);

      // Prepare children data for enrollment
      const childrenData = children.map(child => ({
        name: child.name.trim(),
        dateOfBirth: child.dateOfBirth.toISOString(),
        packageId: child.packageId
      }));

      // Create Stripe checkout session
      const session = await enrollMultipleChildren(
        user.uid,
        childrenData,
        discountCode || null
      );

      // Redirect to Stripe checkout
      const stripe = await stripePromise;
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: session.sessionId
      });

      if (stripeError) {
        throw stripeError;
      }

    } catch (err) {
      console.error('❌ Error enrolling children:', err);
      // A promotion-code restriction failure (Stripe refused the code) belongs
      // next to the coupon field, not as a page-level error.
      if (err && err.discountError) {
        setDiscountValidation({ valid: false, error: err.message });
      } else {
        setError(err.message || 'Failed to process enrollment. Please try again.');
      }
      setIsProcessing(false);
    }
  };

  // Loading — decide nothing yet.
  if (profile.status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Item 2 — an adult account that reached /add-children (bookmark, old link)
  // gets an on-screen explanation and a way back, not a silent bounce.
  // AccountSettings hides the entry button; this is the page defending itself.
  if (accountIsAdult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-md w-full">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-100 mb-4">
            <Users className="text-purple-600" size={26} />
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-3">This page is for family accounts</h1>
          <p className="text-gray-600 mb-6">
            Your account is set up for one adult learner — you. Enrolling additional children needs a
            separate family account. If you'd like to set one up, or you think this is a mistake, email
            us at <a href="mailto:info@my-roots.ca" className="text-purple-600 font-medium">info@my-roots.ca</a>.
          </p>
          <button
            onClick={() => navigate('/account')}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200"
          >
            Back to My Account
          </button>
        </div>
      </div>
    );
  }

  // profile.status === 'loaded' (family / no-doc) OR 'failed' (fail open → family)
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/account')}
            className="p-3 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all duration-200 backdrop-blur-sm"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">Enroll Additional Children</h1>
            <p className="text-purple-200 mt-1">Add 1 to 5 children in a single transaction</p>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-red-800">{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Children Forms */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Users size={24} className="text-purple-600" />
                Children Information
              </h2>
              <span className="text-sm text-gray-600">
                {children.length} / 5 children
              </span>
            </div>

            <div className="space-y-6">
              {children.map((child, index) => (
                <div 
                  key={index} 
                  className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-200"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-purple-600">
                      Child {index + 1}
                    </h3>
                    {children.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveChild(index)}
                        className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 size={20} />
                      </button>
                    )}
                  </div>

                  {/* Name Input */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={child.name}
                      onChange={(e) => updateChild(index, 'name', e.target.value)}
                      placeholder="Enter child's full name"
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        validationErrors[`${index}_name`]
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    />
                    {validationErrors[`${index}_name`] && (
                      <p className="text-red-600 text-sm mt-1">
                        {validationErrors[`${index}_name`]}
                      </p>
                    )}
                  </div>

                  {/* Date of Birth */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Date of Birth *
                    </label>
                    <DatePicker
                      selected={child.dateOfBirth}
                      onChange={(date) => updateChild(index, 'dateOfBirth', date)}
                      dateFormat="MMMM d, yyyy"
                      placeholderText="Select date of birth"
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        validationErrors[`${index}_dateOfBirth`]
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      maxDate={new Date()}
                      showYearDropdown
                      yearDropdownItemNumber={20}
                    />
                    {validationErrors[`${index}_dateOfBirth`] && (
                      <p className="text-red-600 text-sm mt-1">
                        {validationErrors[`${index}_dateOfBirth`]}
                      </p>
                    )}
                  </div>

                  {/* Package Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Course Package *
                    </label>
                    <select
                      value={child.packageId}
                      onChange={(e) => updateChild(index, 'packageId', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {availablePackages.map(pkg => (
                        <option key={pkg.id} value={pkg.id}>
                          {pkg.name} - ${pkg.price} CAD
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Another Child Button */}
            {children.length < 5 && (
              <button
                type="button"
                onClick={handleAddChild}
                className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Add Another Child
              </button>
            )}
          </div>

          {/* Pricing Summary */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <DollarSign size={24} className="text-green-600" />
              Pricing Summary
            </h2>

            {/* Children List */}
            <div className="space-y-3 mb-6">
              {children.map((child, index) => {
                const pkg = COURSE_PACKAGES[child.packageId];
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Crown size={16} className="text-purple-600" />
                      <span className="text-gray-700">
                        {child.name || `Child ${index + 1}`} - {pkg?.name || 'Package'}
                      </span>
                    </div>
                    <span className="font-semibold text-gray-800">
                      ${pkg?.price.toFixed(2)} CAD
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Subtotal */}
            <div className="flex items-center justify-between py-3 border-t border-gray-200">
              <span className="text-gray-700 font-medium">Subtotal</span>
              <span className="font-semibold text-gray-800">
                ${subtotal.toFixed(2)} CAD
              </span>
            </div>

            {/* Discount Code */}
            <div className="mt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Discount Code (Optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value.toUpperCase());
                    setDiscountValidation(null);
                  }}
                  placeholder="Enter discount code"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  type="button"
                  onClick={handleValidateDiscount}
                  disabled={!discountCode.trim() || isValidatingDiscount}
                  className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isValidatingDiscount ? 'Checking...' : 'Apply'}
                </button>
              </div>
              
              {/* Discount Validation Message */}
              {discountValidation && (
                <div className={`mt-2 flex items-center gap-2 ${
                  discountApplies ? 'text-green-600' : 'text-red-600'
                }`}>
                  {discountApplies ? (
                    <CheckCircle size={16} />
                  ) : (
                    <AlertCircle size={16} />
                  )}
                  <span className="text-sm">
                    {!discountValidation.valid
                      ? discountValidation.error
                      : discount.wrongCurrency
                        ? "This code can't be applied to your order."
                        : discountApplies
                          ? `Discount applied! You save $${discountAmount.toFixed(2)}`
                          : 'This code has no effect on your order.'}
                  </span>
                </div>
              )}
            </div>

            {/* Total */}
            <div className="flex items-center justify-between py-4 mt-4 border-t-2 border-gray-300">
              <span className="text-xl font-bold text-gray-800">Total</span>
              <span className="text-2xl font-black text-purple-600">
                ${calculateTotal().toFixed(2)} CAD
              </span>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isProcessing || children.length === 0}
            className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-lg rounded-xl hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
          >
            {isProcessing ? (
              <>
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Processing...
              </>
            ) : (
              <>
                <CheckCircle size={24} />
                Enroll & Pay ${calculateTotal().toFixed(2)}
              </>
            )}
          </button>

          <p className="text-center text-white/80 text-sm mt-4">
            You'll be redirected to Stripe to complete payment securely
          </p>
        </form>
      </div>
    </div>
  );
}

export default AddChildren;